import React from 'react';
import { Icons } from '../../Icon.jsx';
import { DraggableLabel } from '../../DraggableLabel.jsx';
import { getPropertyDefinition } from '../../style-plugins/index.js';
import PropertyControl from './PropertyControl.jsx';
import { cleanLabel } from '../utils.js';

const PropertyRow = ({ propertyKey, activeProps, ownProps, disabledProps, manualProps, onChange, onToggleProperty, onRemoveProperty, pluginName, activeBreakpoint = 'default' }) => {
    const def = getPropertyDefinition(propertyKey);
    if (!def) return null;

    // ownProps = current breakpoint's own properties
    // activeProps = effective/inherited properties (cascaded from lower breakpoints)
    // disabledProps = disabled properties for current breakpoint

    const isOwnActive = ownProps?.hasOwnProperty(propertyKey) && !disabledProps.hasOwnProperty(propertyKey);
    const isInheritedActive = !isOwnActive && activeProps.hasOwnProperty(propertyKey) && !disabledProps.hasOwnProperty(propertyKey);
    const isPropActive = isOwnActive || isInheritedActive;

    const val = ownProps?.[propertyKey] ?? activeProps[propertyKey] ?? disabledProps[propertyKey] ?? '';
    const label = cleanLabel(propertyKey, def.label);

    const isOverridden = activeBreakpoint !== 'default' && isOwnActive;
    const isInherited = isInheritedActive && activeBreakpoint !== 'default';

    return (
        <div className={`flex items-center gap-2 py-1 border-b border-slate-50 last:border-0 ${isPropActive ? '' : 'opacity-60'} ${isOverridden ? 'bg-blue-50/30' : ''}`}>
            <button
                onClick={() => onToggleProperty(propertyKey)}
                className={`p-1 rounded hover:bg-slate-200 shrink-0 ${isPropActive ? 'text-blue-500' : 'text-gray-400'}`}
                title={isPropActive ? "Disable Property" : "Enable Property"}
            >
                {isPropActive ? <Icons.Eye className="w-3.5 h-3.5"/> : <Icons.EyeOff className="w-3.5 h-3.5"/>}
            </button>

            <div className="w-32 shrink-0 flex flex-col justify-center relative">
                <DraggableLabel
                    label={label}
                    value={val}
                    onChange={(v) => {
                        if (!isPropActive) return;
                        onChange(propertyKey, v);
                    }}
                    className={`text-xs font-semibold truncate w-full flex items-center gap-1 ${isPropActive ? 'text-blue-600' : 'text-slate-500'} ${isPropActive ? 'hover:text-blue-800' : ''}`}
                    disabled={!isPropActive}
                >
                    {isOverridden && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" title={`Overridden in ${activeBreakpoint}`} />}
                    <span className="truncate">{label}</span>
                </DraggableLabel>
                {pluginName && (
                    <span className="text-[10px] text-gray-400 truncate w-full leading-tight">
                        {pluginName}
                    </span>
                )}
            </div>

            <div className="w-64 shrink-0">
                <PropertyControl
                    def={def}
                    value={val}
                    onChange={(v) => {
                        if (!isPropActive) return;
                        onChange(propertyKey, v);
                    }}
                    disabled={!isPropActive}
                    className={isInherited ? 'border-dashed border-slate-300 text-slate-500 bg-slate-50' : ''}
                />
            </div>

            <div className="flex-1 flex justify-end">
                {isOverridden && (
                    <button 
                        onClick={(e) => { e.stopPropagation(); onRemoveProperty(propertyKey); }} // Resetting override means removing it from current breakpoint activeProps
                        className="text-[9px] text-red-400 hover:text-red-600 mr-2 opacity-50 hover:opacity-100"
                        title="Reset Override"
                    >
                        Reset
                    </button>
                )}
            </div>

            <button
                onClick={(e) => { e.stopPropagation(); onRemoveProperty(propertyKey); }}
                className="p-1 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 shrink-0"
                title="Remove Property"
            >
                <Icons.Trash className="w-3.5 h-3.5"/>
            </button>
        </div>
    );
};

export default PropertyRow;
